import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, Calendar, Tag, Settings, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ImageUpload from '../components/ImageUpload';

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    page_title: 'Blog',
    page_subtitle: "Latest news, insights, and updates from NYC's Premier Tech Community",
    slug: 'blog',
    meta_title: 'Blog | Pulse NYC',
    meta_description: "Latest news, insights, and updates from NYC's Premier Tech Community.",
    posts_per_page: 12
  });
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    author: '',
    category: '',
    tags: [],
    meta_title: '',
    meta_description: '',
    status: 'draft',
    published_at: ''
  });

  useEffect(() => {
    fetchPosts();
    fetchPageSettings();
  }, []);

  const fetchPageSettings = async () => {
    try {
      const response = await api.get('/admin/blog/settings');
      setPageSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch blog page settings:', error);
    }
  };

  const savePageSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/blog/settings', pageSettings);
      toast({ title: 'Success', description: 'Blog page settings saved' });
      setShowSettings(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save blog page settings',
        variant: 'destructive',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await api.get('/admin/blog');
      setPosts(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch blog posts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingPost ? formData.slug : generateSlug(title)
    });
  };

  const handleCreate = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featured_image: '',
      author: '',
      category: '',
      tags: [],
      meta_title: '',
      meta_description: '',
      status: 'draft'
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    // Format published_at date for datetime-local input
    let publishedAtFormatted = '';
    if (post.published_at) {
      const date = new Date(post.published_at);
      publishedAtFormatted = date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
    }
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content,
      featured_image: post.featured_image || '',
      author: post.author || '',
      category: post.category || '',
      tags: post.tags || [],
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      status: post.status,
      published_at: publishedAtFormatted
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.slug || !formData.content) {
      toast({
        title: 'Error',
        description: 'Title, slug, and content are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingPost) {
        await api.put(`/admin/blog/${editingPost.id}`, formData);
        toast({ title: 'Success', description: 'Blog post updated' });
      } else {
        await api.post('/admin/blog', formData);
        toast({ title: 'Success', description: 'Blog post created' });
      }
      setIsDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save blog post',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      await api.delete(`/admin/blog/${postId}`);
      toast({ title: 'Success', description: 'Blog post deleted' });
      fetchPosts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete blog post',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/admin/blog/${post.id}`, { status: newStatus });
      toast({ 
        title: 'Success', 
        description: `Blog post ${newStatus === 'published' ? 'published' : 'unpublished'}` 
      });
      fetchPosts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || post.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
          <p className="text-gray-600 mt-2">
            Manage your blog content • {posts.length} posts
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowSettings(!showSettings)} variant="outline">
            <Settings size={20} className="mr-2" />
            Page Settings
          </Button>
          <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
            <Plus size={20} className="mr-2" />
            New Post
          </Button>
        </div>
      </div>

      {/* Page Settings Card */}
      {showSettings && (
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Blog Page Settings</h2>
            <Button onClick={savePageSettings} disabled={savingSettings} className="bg-black hover:bg-gray-800">
              <Save size={16} className="mr-2" />
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
                <Input
                  value={pageSettings.page_title}
                  onChange={(e) => setPageSettings({ ...pageSettings, page_title: e.target.value })}
                  placeholder="Blog"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Subtitle</label>
                <Textarea
                  value={pageSettings.page_subtitle}
                  onChange={(e) => setPageSettings({ ...pageSettings, page_subtitle: e.target.value })}
                  placeholder="Latest news and updates..."
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Posts Per Page</label>
                <Select
                  value={String(pageSettings.posts_per_page)}
                  onValueChange={(value) => setPageSettings({ ...pageSettings, posts_per_page: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6</SelectItem>
                    <SelectItem value="9">9</SelectItem>
                    <SelectItem value="12">12 (Default)</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="18">18</SelectItem>
                    <SelectItem value="24">24</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title (SEO)</label>
                <Input
                  value={pageSettings.meta_title}
                  onChange={(e) => setPageSettings({ ...pageSettings, meta_title: e.target.value })}
                  placeholder="Blog | Pulse NYC"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description (SEO)</label>
                <Textarea
                  value={pageSettings.meta_description}
                  onChange={(e) => setPageSettings({ ...pageSettings, meta_description: e.target.value })}
                  placeholder="SEO description..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Posts</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-500 mb-4">No blog posts found</p>
            <Button onClick={handleCreate} variant="outline">
              <Plus size={18} className="mr-2" />
              Create Your First Post
            </Button>
          </Card>
        ) : (
          filteredPosts.map((post) => (
            <Card key={post.id} className="p-4" data-testid={`blog-post-${post.id}`}>
              <div className="flex items-start gap-4">
                {post.featured_image && (
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                    <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                      {post.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">/{post.slug}</p>
                  {post.excerpt && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{post.excerpt}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {post.category && (
                      <span className="flex items-center gap-1">
                        <Tag size={12} />
                        {post.category}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(post.published_at || post.created_at)}
                    </span>
                    {post.author && <span>by {post.author}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleStatus(post)}
                    title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                  >
                    {post.status === 'published' ? <Eye size={18} /> : <EyeOff size={18} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(post)}
                  >
                    <Edit size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(post.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <Input
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="Enter post title"
                  data-testid="blog-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug *
                </label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="url-friendly-slug"
                  data-testid="blog-slug-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt
              </label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief summary of the post..."
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content * (HTML supported)
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your blog post content here... HTML tags are supported."
                rows={12}
                className="font-mono text-sm"
                data-testid="blog-content-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image
              </label>
              <ImageUpload
                value={formData.featured_image}
                onChange={(url) => setFormData({ ...formData, featured_image: url })}
                label="Featured Image"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author
                </label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Author name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., News, Events"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Publish Date
              </label>
              <Input
                type="datetime-local"
                value={formData.published_at}
                onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                className="max-w-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set a custom publish date for sorting. Leave empty to use creation date.
              </p>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-4">SEO Settings</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Title
                  </label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="SEO title (defaults to post title)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description
                  </label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="SEO description (defaults to excerpt)"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-black hover:bg-gray-800">
              {editingPost ? 'Update Post' : 'Create Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
